import React, { useEffect } from 'react'
import {
  Card,
  Form,
  Select,
  Button,
  InputNumber,
  Input,
  message,
  Switch,
  Modal
} from 'antd'
import styled from 'styled-components'
import api from '../utils/api'
import { useAuth } from '../hooks/AuthContext'
import Hint from './common/Hint'
import { DEFAULT_TIMEZONE, setBusinessTimezone } from '../utils/timezone'

const { Option } = Select

/**
 * 可選的營運時區。
 *
 * 刻意不列出全部 400 多個 IANA 時區 —— 店家看到那種清單只會不敢動。
 * 這裡只放台灣＋鄰近常見的幾個，未來真的有需要再加。
 * value 一定要是 IANA 名稱（後端會用 Intl 驗證）。
 */
const TIMEZONE_OPTIONS = [
  { value: 'Asia/Taipei', label: '台灣（UTC+8）' },
  { value: 'Asia/Shanghai', label: '中國（UTC+8）' },
  { value: 'Asia/Hong_Kong', label: '香港（UTC+8）' },
  { value: 'Asia/Singapore', label: '新加坡（UTC+8）' },
  { value: 'Asia/Kuala_Lumpur', label: '馬來西亞（UTC+8）' },
  { value: 'Asia/Tokyo', label: '日本（UTC+9）' },
  { value: 'Asia/Seoul', label: '韓國（UTC+9）' },
  { value: 'Asia/Bangkok', label: '泰國／越南（UTC+7）' },
  { value: 'Asia/Jakarta', label: '印尼（雅加達，UTC+7）' },
  { value: 'Australia/Sydney', label: '澳洲（雪梨）' },
  { value: 'America/Los_Angeles', label: '美國西岸' },
  { value: 'America/New_York', label: '美國東岸' }
]

/**
 * 「照這個時區，現在是幾點」。
 *
 * 刻意直接用 Intl 而不是共用的 tzNow()：這裡要預覽的是「選單上當下選的」
 * 時區，還沒存檔，全域設定仍然是舊的值。
 */
const nowTextInTimezone = (tz) => {
  if (!tz) return null
  try {
    return new Intl.DateTimeFormat('zh-TW', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).format(new Date())
  } catch (e) {
    return null
  }
}

const Container = styled.div`
  max-width: 1000px;
  margin: auto;
  padding: 10px 0;
`
const ButtonGroup = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
`
const StyledButton = styled(Button)`
  width: 100%;
  height: 50px;
  font-size: 16px;
`
const SectionCard = styled(Card)`
  margin-bottom: 20px;
`

const formItemLayout = {
  labelCol: {
    span: 8
  },
  wrapperCol: {
    span: 16
  }
}

const timeOptions = [
  { key: 'unlimited', value: 'unlimited', label: '無限制' },
  ...Array.from({ length: 3 }, (_, i) => ({
    key: `${(i + 1) * 15}`,
    value: (i + 1) * 15,
    label: `${(i + 1) * 15} 分鐘`
  })),
  // 小時級距開到 12 小時：原本只到 7 小時，中間就直接跳到「1 天」，
  // 想設「提前 10 小時」的店家只能選 1 天，而 1 天是從「此刻」往後推 24 小時，
  // 實際效果會把隔天的白天時段整片擋掉。
  ...Array.from({ length: 12 }, (_, i) => ({
    key: `${(i + 1) * 60}`,
    value: (i + 1) * 60,
    label: `${i + 1} 小時`
  })),
  ...Array.from({ length: 10 }, (_, i) => ({
    key: `${(i + 1) * 1440}`,
    value: (i + 1) * 1440,
    label: `${i + 1} 天`
  }))
]

const convertValue = (value) => {
  if (value === 0) return 'unlimited'
  return value
}

const convertInputValue = (value) => {
  if (value === 'unlimited') return 0
  return value
}

const AppointmentSettings = () => {
  const [form] = Form.useForm()
  const { user, checkBusinessStatus } = useAuth()
  const businessId = user.businessId
  // 讓店家在選單旁邊直接看到「照這個時區，現在是幾點」，改錯的話一眼就發現
  const selectedTimezone = Form.useWatch('timezone', form)
  const bookingOpen = Form.useWatch('onlineBookingEnabled', form)
  // 系統層級的關閉（合約到期／平台終止服務）—— 這種情況店家自己打開開關也沒用
  const [systemBlock, setSystemBlock] = React.useState(null)

  /**
   * 關閉線上預約前先確認。
   *
   * 這個開關的影響範圍比其他設定大一個量級 —— 一關掉，客人就完全送不出
   * 新的預約。誤觸的話店家不會馬上發現（畫面上只是一個 Switch 變灰），
   * 直到某天覺得「怎麼都沒人約」。所以關的時候擋一下，開的時候不擋。
   */
  const handleBookingSwitch = (checked) => {
    if (checked) {
      form.setFieldsValue({ onlineBookingEnabled: true })
      return
    }
    Modal.confirm({
      title: '要暫停線上預約嗎？',
      content:
        '暫停後客人在網站與 LINE 上都無法送出新的預約，你的店家頁面仍然看得到，已成立的預約也不受影響。隨時可以再打開。',
      okText: '暫停預約',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: () => form.setFieldsValue({ onlineBookingEnabled: false })
    })
  }

  const fetchAppointmentSettings = React.useCallback(async () => {
    try {
      const { data } = await api.get(
        `/api/business_B/${businessId}/appointment-settings`
      )
      const appointmentSettings = data.data
      form.setFieldsValue({
        ...appointmentSettings,
        minBookingTime: convertValue(appointmentSettings.minBookingTime),
        minCancelTime: convertValue(appointmentSettings.minCancelTime),
        minRescheduleTime: convertValue(appointmentSettings.minRescheduleTime),
        unspecifiedStylistEnabled: appointmentSettings.unspecifiedStylistEnabled || false,
        // 舊資料沒有這個欄位時視為開放，不能用 || false（會把沒設定的店家變成關閉）
        onlineBookingEnabled:
          appointmentSettings.onlineBookingEnabled !== false,
        onlineBookingPausedReason:
          appointmentSettings.onlineBookingPausedReason || '',
        showFullDayTimeline: appointmentSettings.showFullDayTimeline === true,
        homeCalendarView: appointmentSettings.homeCalendarView || 'dayGridMonth',
        timezone: appointmentSettings.timezone || 'Asia/Taipei'
      })
      const access = appointmentSettings.bookingAccess
      setSystemBlock(
        access?.closed && access.reason !== 'paused' ? access : null
      )
    } catch (error) {
      message.error('無法獲取預約設置')
      console.error('Error fetching appointment settings:', error)
    }
  }, [businessId, form])

  useEffect(() => {
    fetchAppointmentSettings()
  }, [fetchAppointmentSettings])

  const handleFinish = async (values) => {
    try {
      const updatedValues = {
        ...values,
        minBookingTime: convertInputValue(values.minBookingTime),
        minCancelTime: convertInputValue(values.minCancelTime),
        minRescheduleTime: convertInputValue(values.minRescheduleTime)
      }
      await api.put(
        `/api/business_B/${businessId}/appointment-settings`,
        updatedValues
      )
      // 時區是全站共用的，存完要立刻生效，不能等下次重新整理 —— 否則店家
      // 剛改完卻看到畫面上還是舊時區的時間，會以為沒存到。
      if (updatedValues.timezone) {
        setBusinessTimezone(updatedValues.timezone)
        // 同步 AuthContext 的 user.timezone（首頁日曆是讀那個值）
        await checkBusinessStatus()
      }
      message.success('預約設置已保存')
    } catch (error) {
      message.error('保存預約設置失敗')
      console.error('Error saving appointment settings:', error)
    }
  }

  return (
    <Container>
      <Form
          {...formItemLayout}
          form={form}
          onFinish={handleFinish}
          initialValues={{
            defaultStatus: 'confirmed',
            advanceBookingDays: 100,
            bufferMinutes: 0,
            maxParticipantsPerBooking: 4,
            unspecifiedStylistEnabled: false,
            onlineBookingEnabled: true,
            onlineBookingPausedReason: '',
            showFullDayTimeline: false,
            homeCalendarView: 'dayGridMonth',
            timezone: DEFAULT_TIMEZONE
          }}
        >
        <SectionCard title='線上預約'>
          {systemBlock && (
            <Hint tone='error' title='線上預約目前已停止'>
              {systemBlock.internalReason}
              {systemBlock.reason === 'contract_expired'
                ? '。請聯繫平台完成續約，續約後會立即恢復，下面的開關在這之前不會生效。'
                : '。如有疑問請聯繫平台客服。'}
            </Hint>
          )}
          {bookingOpen === false && !systemBlock && (
            <Hint tone='warning' title='目前暫停接受線上預約'>
              客人在網站與 LINE 上都送不出新的預約。你的店家頁面仍然看得到，
              已經成立的預約也不受影響，隨時可以再打開。
            </Hint>
          )}
          <Form.Item
            name='onlineBookingEnabled'
            label='接受線上預約'
            valuePropName='checked'
            extra='公休、出國或人手不足時可以先關起來。關閉期間客人無法送出新的預約，既有預約與店家頁面都不受影響。'
          >
            <Switch
              checkedChildren='開放中'
              unCheckedChildren='已暫停'
              disabled={!!systemBlock}
              onChange={handleBookingSwitch}
            />
          </Form.Item>
          {bookingOpen === false && !systemBlock && (
            <Form.Item
              name='onlineBookingPausedReason'
              label='暫停原因（選填）'
              extra='會顯示給客人看，例如「7/1–7/10 出國進修」。留空則顯示「店家目前暫停接受線上預約」。'
            >
              <Input maxLength={100} placeholder='例：7/1–7/10 出國進修' />
            </Form.Item>
          )}
        </SectionCard>

        <SectionCard title='預約設定'>
          <Form.Item name='defaultStatus' label='預設預約完成狀態'>
            <Select>
              <Option value='confirmed'>已完成</Option>
              <Option value='pending'>確認中</Option>
            </Select>
          </Form.Item>
          <Form.Item name='minBookingTime' label='最晚預訂時間'>
            <Select>
              {timeOptions.map((option) => (
                <Option key={option.key} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name='minCancelTime' label='最晚取消預訂時間'>
            <Select>
              {timeOptions.map((option) => (
                <Option key={option.key} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name='minRescheduleTime' label='最晚更改預訂時間'>
            <Select>
              {timeOptions.map((option) => (
                <Option key={option.key} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name='advanceBookingDays' label='可提前預訂的天數'>
            <InputNumber min={1} max={365} />
          </Form.Item>
          <Form.Item
            name='bufferMinutes'
            label='整理時間（分鐘）'
            extra='每筆預約結束後保留的整理／銜接時間，這段時間內不接受下一筆預約。0 = 不留整理時間。可在個別「服務編輯」頁針對特殊服務覆寫。'
          >
            <InputNumber min={0} max={240} step={5} addonAfter='分鐘' />
          </Form.Item>
          <Form.Item
            name='maxParticipantsPerBooking'
            label='單筆預約人數上限'
            extra='客人單筆預約最多可選的人數。單人服務的店家建議設為 1，前台將不再顯示人數選擇。'
          >
            <Select>
              <Option value={1}>1 位（單人服務）</Option>
              <Option value={2}>最多 2 位</Option>
              <Option value={3}>最多 3 位</Option>
              <Option value={4}>最多 4 位</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name='unspecifiedStylistEnabled'
            label='允許不指定人員'
            valuePropName='checked'
            extra='開啟後，客人在前台預約時可以選擇不指定人員，由店家稍後在「待指派預約」指派。能不能約得到，是看該時段有沒有排班、且排班有涵蓋這個服務的員工 —— 不需要再逐項服務設定。'
          >
            <Switch />
          </Form.Item>
          <Form.Item
            name='showFullDayTimeline'
            label='首頁顯示完整 24 小時'
            valuePropName='checked'
            extra='關閉時（預設），首頁的週／日檢視只畫營業時間前後各一小時，深夜那段收起來，不用捲很久才看到第一筆預約。落在收合範圍外的預約、休假與排班仍會自動撐開時間軸，不會有單子被藏起來。24 小時營業或常態接深夜單再打開。'
          >
            <Switch />
          </Form.Item>
          <Form.Item
            name='homeCalendarView'
            label='首頁預設檢視'
            extra='打開首頁時預約日曆先顯示哪一種。每天主要看當日排程的店家可以選「日」；進到首頁後仍可隨時切換月／週／日。'
          >
            <Select>
              <Option value='dayGridMonth'>月（預設）</Option>
              <Option value='timeGridWeek'>週</Option>
              <Option value='timeGridDay'>日</Option>
            </Select>
          </Form.Item>
        </SectionCard>

        <SectionCard title='營運時區'>
          <Hint tone='info' title='出國也看得到店裡的時間'>
            後台所有的時間 —— 預約、結帳、報表、休假 ——
            一律以這裡設定的時區顯示與計算，跟你現在人在哪裡、
            用哪一台電腦無關。在國外開後台看到的仍然是店裡的時間。
          </Hint>
          <Form.Item
            name='timezone'
            label='店家所在時區'
            rules={[{ required: true, message: '請選擇時區' }]}
            extra={
              nowTextInTimezone(selectedTimezone)
                ? `依這個時區，現在是 ${nowTextInTimezone(selectedTimezone)}。改動會影響全店所有人看到的時間，已經存好的預約日期時間不會被改寫。`
                : '改動會影響全店所有人看到的時間，已經存好的預約日期時間不會被改寫。'
            }
          >
            <Select showSearch optionFilterProp='children'>
              {TIMEZONE_OPTIONS.map((tz) => (
                <Option key={tz.value} value={tz.value}>
                  {tz.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </SectionCard>

        <ButtonGroup>
          <StyledButton type='primary' htmlType='submit'>
            保存
          </StyledButton>
        </ButtonGroup>
      </Form>
    </Container>
  )
}

export default AppointmentSettings
